import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type JSX, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, Maximize2, Minimize2, Move, PanelBottom, PanelLeft, PanelRight, PanelTop, Pin, PinOff, RotateCcw, X } from 'lucide-react';
import { useFloatingWindowStore, type FloatingDockState, type FloatingWindowRect } from '../../stores/floatingWindow.store.js';

interface FloatingWindowFrameProps {
  id: string;
  title: string;
  group?: string;
  defaultRect: FloatingWindowRect;
  children: ReactNode;
  defaultOpen?: boolean;
  canMaximize?: boolean;
  keyboardClosable?: boolean;
  important?: boolean;
  helpText?: string;
  accent?: string;
  className?: string;
  contentStyle?: CSSProperties;
  onOpen?: () => void;
  onClose?: () => void;
  onMinimize?: () => void;
  onRestore?: () => void;
}

const chromeButton: CSSProperties = {
  height: 24,
  width: 24,
  display: 'grid',
  placeItems: 'center',
  borderRadius: 6,
  border: '1px solid rgba(148,163,184,0.12)',
  background: 'rgba(8,14,30,0.55)',
  color: 'rgba(203,213,225,0.68)',
  cursor: 'pointer',
};

function useViewportTick(): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    const onResize = () => setTick((v) => v + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
}

function maximizedRect(): FloatingWindowRect {
  return { x: 8, y: 58, width: Math.max(320, window.innerWidth - 16), height: Math.max(240, window.innerHeight - 92) };
}

function dockRect(dock: FloatingDockState, fallback: FloatingWindowRect): FloatingWindowRect {
  if (dock === 'floating') return fallback;
  const margin = 12;
  const top = 58;
  const bottom = 34;
  const width = Math.max(320, Math.min(460, window.innerWidth * 0.32));
  const height = Math.max(220, Math.min(360, window.innerHeight * 0.34));
  if (dock === 'left') return { x: margin, y: top, width, height: window.innerHeight - top - bottom - margin };
  if (dock === 'right') return { x: window.innerWidth - width - margin, y: top, width, height: window.innerHeight - top - bottom - margin };
  if (dock === 'top') return { x: margin, y: top, width: window.innerWidth - margin * 2, height };
  return { x: margin, y: window.innerHeight - height - bottom, width: window.innerWidth - margin * 2, height };
}

export function FloatingWindowFrame({
  id,
  title,
  group = 'Workspace',
  defaultRect,
  defaultOpen = false,
  canMaximize = true,
  keyboardClosable = true,
  important = false,
  helpText,
  accent = '#5eead4',
  className,
  contentStyle,
  onOpen,
  onClose,
  onMinimize,
  onRestore,
  children,
}: FloatingWindowFrameProps): JSX.Element | null {
  const registerWindow = useFloatingWindowStore((s) => s.registerWindow);
  const unregisterWindow = useFloatingWindowStore((s) => s.unregisterWindow);
  const win = useFloatingWindowStore((s) => s.windows[id]);
  const activeWindowId = useFloatingWindowStore((s) => s.activeWindowId);
  const bringToFront = useFloatingWindowStore((s) => s.bringToFront);
  const updateGeometry = useFloatingWindowStore((s) => s.updateGeometry);
  const closeWindow = useFloatingWindowStore((s) => s.closeWindow);
  const minimizeWindow = useFloatingWindowStore((s) => s.minimizeWindow);
  const togglePinned = useFloatingWindowStore((s) => s.togglePinned);
  const toggleMaximized = useFloatingWindowStore((s) => s.toggleMaximized);
  const resetWindow = useFloatingWindowStore((s) => s.resetWindow);
  const dockWindow = useFloatingWindowStore((s) => s.dockWindow);
  const nudgeActiveWindow = useFloatingWindowStore((s) => s.nudgeActiveWindow);
  const dragRef = useRef<{ sx: number; sy: number; start: FloatingWindowRect } | null>(null);
  const resizeRef = useRef<{ sx: number; sy: number; start: FloatingWindowRect } | null>(null);
  useViewportTick();

  useEffect(() => {
    registerWindow({ id, title, group, defaultRect, defaultOpen, canMaximize, keyboardClosable, important, helpText, onOpen, onClose, onMinimize, onRestore });
    return () => unregisterWindow(id);
  }, [id, title, group, defaultRect.x, defaultRect.y, defaultRect.width, defaultRect.height, defaultOpen, canMaximize, keyboardClosable, important, helpText, onOpen, onClose, onMinimize, onRestore, registerWindow, unregisterWindow]);

  const rect = useMemo(() => {
    if (!win) return defaultRect;
    if (win.maximized) return maximizedRect();
    return dockRect(win.dock, win.rect);
  }, [win, defaultRect]);

  const startDrag = useCallback((e: React.PointerEvent) => {
    if (!win || win.maximized) return;
    const target = e.target as HTMLElement;
    if (target.closest('button, input, textarea, select, [data-no-window-drag="true"]')) return;
    e.preventDefault();
    bringToFront(id);
    dragRef.current = { sx: e.clientX, sy: e.clientY, start: rect };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }, [bringToFront, id, rect, win]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (dragRef.current) {
      const { sx, sy, start } = dragRef.current;
      updateGeometry(id, { ...start, x: start.x + e.clientX - sx, y: start.y + e.clientY - sy }, false);
    }
    if (resizeRef.current) {
      const { sx, sy, start } = resizeRef.current;
      updateGeometry(id, { ...start, width: Math.max(220, start.width + e.clientX - sx), height: Math.max(150, start.height + e.clientY - sy) }, false);
    }
  }, [id, updateGeometry]);

  const stopPointer = useCallback(() => {
    if (dragRef.current && win) updateGeometry(id, win.rect, true);
    if (resizeRef.current && win) updateGeometry(id, win.rect, true);
    dragRef.current = null;
    resizeRef.current = null;
  }, [id, updateGeometry, win]);

  const startResize = useCallback((e: React.PointerEvent) => {
    if (!win || win.maximized) return;
    e.preventDefault();
    e.stopPropagation();
    bringToFront(id);
    resizeRef.current = { sx: e.clientX, sy: e.clientY, start: rect };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }, [bringToFront, id, rect, win]);

  useEffect(() => {
    if (activeWindowId !== id) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inTextInput = !!target?.closest('input, textarea, [contenteditable="true"]');
      if (e.key === 'Escape' && keyboardClosable && !inTextInput) {
        e.preventDefault();
        closeWindow(id);
      }
      if ((e.altKey || (e.ctrlKey && e.shiftKey)) && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        const step = e.shiftKey ? 24 : 8;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        nudgeActiveWindow(dx, dy);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeWindowId, closeWindow, id, keyboardClosable, nudgeActiveWindow]);

  if (!win?.open || win.minimized) return null;

  const frame = (
    <div
      data-testid={`floating-window-${id}`}
      data-r2-floating-window={id}
      className={className}
      onMouseDown={() => bringToFront(id)}
      onPointerMove={onPointerMove}
      onPointerUp={stopPointer}
      style={{
        position: 'fixed', left: rect.x, top: rect.y, width: rect.width, height: rect.height,
        zIndex: win.zIndex + (win.pinned ? 50000 : 0), display: 'flex', flexDirection: 'column', overflow: 'hidden',
        borderRadius: 10, background: 'rgba(4,8,18,0.97)', border: `1px solid ${activeWindowId === id ? `${accent}66` : 'rgba(148,163,184,0.18)'}`,
        boxShadow: activeWindowId === id ? `0 20px 70px rgba(0,0,0,0.72), 0 0 22px ${accent}18` : '0 16px 52px rgba(0,0,0,0.56)',
        backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', transition: dragRef.current || resizeRef.current ? 'none' : 'box-shadow 140ms ease, border-color 140ms ease',
      }}
    >
      <div
        data-testid={`floating-window-${id}-drag-handle`}
        onPointerDown={startDrag}
        style={{ height: 38, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 9, padding: '0 9px 0 12px', cursor: win.maximized ? 'default' : 'grab', background: `linear-gradient(90deg, ${accent}16, rgba(8,14,30,0.92) 40%, rgba(8,14,30,0.75))`, borderBottom: '1px solid rgba(148,163,184,0.12)', userSelect: 'none' }}
      >
        <Move size={13} style={{ color: accent, flexShrink: 0 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
          <div style={{ fontSize: 8, color: 'rgba(148,163,184,0.48)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{win.dock === 'floating' ? group : `Docked · ${win.dock}`}</div>
        </div>
        {helpText && <button data-testid={`floating-window-${id}-help`} title={helpText} style={chromeButton}><HelpCircle size={12} /></button>}
        <button data-testid={`floating-window-${id}-dock-left`} title="Dock left" onClick={() => dockWindow(id, win.dock === 'left' ? 'floating' : 'left')} style={chromeButton}><PanelLeft size={12} /></button>
        <button data-testid={`floating-window-${id}-dock-right`} title="Dock right" onClick={() => dockWindow(id, win.dock === 'right' ? 'floating' : 'right')} style={chromeButton}><PanelRight size={12} /></button>
        <button data-testid={`floating-window-${id}-dock-top`} title="Dock top" onClick={() => dockWindow(id, win.dock === 'top' ? 'floating' : 'top')} style={chromeButton}><PanelTop size={12} /></button>
        <button data-testid={`floating-window-${id}-dock-bottom`} title="Dock bottom" onClick={() => dockWindow(id, win.dock === 'bottom' ? 'floating' : 'bottom')} style={chromeButton}><PanelBottom size={12} /></button>
        <button data-testid={`floating-window-${id}-pin`} title={win.pinned ? 'Unpin' : 'Pin inside BIM OPS'} onClick={() => togglePinned(id)} style={{ ...chromeButton, color: win.pinned ? accent : chromeButton.color }}>{win.pinned ? <PinOff size={12} /> : <Pin size={12} />}</button>
        <button data-testid={`floating-window-${id}-minimize`} title="Minimize" onClick={() => minimizeWindow(id)} style={chromeButton}><Minimize2 size={12} /></button>
        {canMaximize && <button data-testid={`floating-window-${id}-maximize`} title={win.maximized ? 'Restore size' : 'Maximize'} onClick={() => toggleMaximized(id)} style={chromeButton}><Maximize2 size={12} /></button>}
        <button data-testid={`floating-window-${id}-reset`} title="Reset position" onClick={() => resetWindow(id)} style={chromeButton}><RotateCcw size={12} /></button>
        <button data-testid={`floating-window-${id}-close`} title="Close" onClick={() => closeWindow(id)} style={{ ...chromeButton, color: '#fca5a5' }}><X size={13} /></button>
      </div>
      <div data-testid={`floating-window-${id}-content`} style={{ flex: 1, minHeight: 0, overflow: 'auto', ...contentStyle }}>{children}</div>
      {!win.maximized && win.dock === 'floating' && (
        <div data-testid={`floating-window-${id}-resize-handle`} onPointerDown={startResize} style={{ position: 'absolute', right: 0, bottom: 0, width: 18, height: 18, cursor: 'nwse-resize', background: `linear-gradient(135deg, transparent 45%, ${accent}55 46%, ${accent}55 58%, transparent 59%)` }} />
      )}
    </div>
  );
  return createPortal(frame, document.body);
}
